import { Ref } from 'vue'
import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import dataQueryManager from '../../managers/network/DataQueryManager'
import identityManager from '../../managers/identity/IdentityManager'
import { VaFile } from 'vuestic-ui'

export enum DocumentFolders {
  ENROLAMIENTO_FOTO = 6,
  ENROLAMIENTO_DOCS = 7,
  EMBARCACIONES_FOTO = 8,
  EMBARCACIONES_DOCS = 9,
  ARMADORES_FOTO = 10,
  ARMADORES_DOCS = 11,
  TRIPULANTES_FOTO = 12,
  TRIPULANTES_DOCS = 13,
}

export enum DocumentModules {
  ENROLAMIENTO = 'ENROLAMIENTO',
  EMBARCACIONES = 'EMBARCACIONES',
  ARMADORES = 'ARMADORES',
  TRIPULANTES = 'TRIPULANTES',
}

// Asociar modulo con carpeta
export const ModuleFolderMap: Record<DocumentModules, DocumentFolders[]> = {
  [DocumentModules.ENROLAMIENTO]: [DocumentFolders.ENROLAMIENTO_FOTO, DocumentFolders.ENROLAMIENTO_DOCS],
  [DocumentModules.EMBARCACIONES]: [DocumentFolders.EMBARCACIONES_FOTO, DocumentFolders.EMBARCACIONES_DOCS],
  [DocumentModules.ARMADORES]: [DocumentFolders.ARMADORES_FOTO, DocumentFolders.ARMADORES_DOCS],
  [DocumentModules.TRIPULANTES]: [DocumentFolders.TRIPULANTES_FOTO, DocumentFolders.TRIPULANTES_DOCS],
}

export interface IDocumento {
  id: number
  vencimiento: string | null
  id_subida: number
  modulo: DocumentModules
  id_gestor: number
  tipo_formato: string
  id_usuario: number
}

export interface IFileData {
  file: VaFile | number
  expireDate: Date | null
}

export default class DocumentsServices {
  private bucketID: string | null = null

  constructor() {
    // Registrar el bucket para documentos
    this.bucketID = dataStorageManager.registerBucket<IDocumento>(
      this.getAllDocuments.bind(this), // Asegúrate de enlazar el contexto correcto
      {
        refreshRatio: 5 * 60 * 1000, // Refrescar cada 5 minutos
        //preload: true, // Preload
        bucketID: BucketDefaultIDs.DOCUMENTOS, // ID del bucket
        operations: new Map<string, DMS_Operation>([
          [
            'insertDocumento',
            {
              operation: this.enviarArchivosAGestor.bind(this),
              reloadAfter: true,
            },
          ],
        ]), // Las operaciones se añadiran despues
      },
    )
  }

  startup() {}

  async getAllDocuments(): Promise<IDocumento[]> {
    try {
      const response = await dataQueryManager.addQuery<IDocumento | IDocumento[]>({
        query: `${import.meta.env.VITE_BASE_URL}/bitacora/1/op_select_documento`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
      })
      if (response.data && Array.isArray(response.data)) {
        return response.data
      } else if (response.data) {
        return [response.data]
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    }
    return []
  }

  async enviarArchivosAGestor(archivoBase64: string, nombreArchivo: string, carpeta: DocumentFolders): Promise<any> {
    const url = 'https://cors.biznet.cl:8080/https://docmain.biznet.cl/websrv/sw/files_muelle_api.php'
    const user = identityManager.getUser()
    const usuario = user ? user.id_usuario.toString() : '-1'

    const body = new URLSearchParams()
    body.set('file', archivoBase64)
    body.set('nombre_file', nombreArchivo)
    body.set('NROFOLDER', carpeta.toString())
    body.set('USUARIO', 'webuser')
    body.set('TAGS', '3333')

    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }

    return fetch(url, {
      method: 'POST',
      headers: headers,
      body: body.toString(),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status == '1') {
          const docId = data.new_document_id
          const modulo = Object.keys(ModuleFolderMap).find((mod) => {
            return ModuleFolderMap[mod as DocumentModules].includes(carpeta)
          }) as DocumentModules

          const documento: IDocumento = {
            id: 0,
            vencimiento: new Date().toISOString(),
            id_subida: docId,
            modulo: modulo,
            id_gestor: 0,
            tipo_formato: nombreArchivo.split('.').pop() || '',
            id_usuario: parseInt(usuario),
          }
          this.insertDocumento(documento)

          return docId
        } else {
          throw new Error('Error al subir el archivo al gestor de documentos')
        }
      })
      .catch((error) => {
        throw error
      })
  }

  async insertDocumento(documentData: Omit<IDocumento, 'id'>): Promise<boolean> {
    try {
      const body = {
        op_insert_documento: {
          id_subida: documentData.id_subida,
          fecha_vencimiento: documentData.vencimiento,
          modulo: documentData.modulo,
          id_gestor: documentData.id_gestor,
          tipo_formato: documentData.tipo_formato,
          id_usuario: documentData.id_usuario,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/bitacora/1/op_insert_documento`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })
      if (response.data && typeof response.data === 'number') {
        return true
      }
    } catch (error) {
      console.error('Error inserting documento:', error)
    }
    return false
  }
}
